import type { AssignmentConflictResponseDto } from '@evimo/client/models';
import type { WizardData } from '../types';
import type {
  SelectOption,
  StepAssetsCanProceedInput,
  StepAssetsFilterState,
} from './types';

/** Normalize antd DatePicker `dateString` (string | string[]) to ISO YYYY-MM-DD. */
export function normalizeDatePickerString(
  dateString: string | string[] | null | undefined,
): string {
  if (Array.isArray(dateString)) {
    const first = dateString.find(
      (value) => typeof value === 'string' && value.trim(),
    );
    return first?.trim() ?? '';
  }
  if (typeof dateString !== 'string') return '';
  const trimmed = dateString.trim();
  if (!trimmed) return '';
  // Accept full ISO timestamps; keep date portion only.
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }
  return trimmed;
}

export function emptyStepAssetsFilters(): StepAssetsFilterState {
  return {
    assetTypeIds: [],
    assetModelIds: [],
    landlordOrganizationIds: [],
    siteIds: [],
  };
}

function nonEmptyIds(ids: string[] | undefined): ids is string[] {
  return Array.isArray(ids) && ids.length > 0;
}

/**
 * Normalize antd Select `mode="multiple"` onChange values to `string[]`.
 * Accepts a bare string (defensive) so the first selection always becomes `[id]`.
 */
export function normalizeIdArray(value: unknown): string[] {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) {
    return value.filter(
      (id): id is string => typeof id === 'string' && id.length > 0,
    );
  }
  if (typeof value === 'string' && value.length > 0) return [value];
  return [];
}

/** Stable, order-independent fingerprint for React Query keys. */
export function serializeIdList(ids: string[] | undefined): string {
  if (!nonEmptyIds(ids)) return '';
  return [...ids].sort().join(',');
}

/** True when both id lists contain the same set of values (order-independent). */
export function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

/** Strip empty lists so query params only send set filters. */
export function toAssignableAssetFilters(filters: StepAssetsFilterState) {
  return {
    ...(nonEmptyIds(filters.assetTypeIds)
      ? { assetTypeIds: [...filters.assetTypeIds] }
      : {}),
    ...(nonEmptyIds(filters.assetModelIds)
      ? { assetModelIds: [...filters.assetModelIds] }
      : {}),
    ...(nonEmptyIds(filters.landlordOrganizationIds)
      ? { landlordOrganizationIds: [...filters.landlordOrganizationIds] }
      : {}),
    ...(nonEmptyIds(filters.siteIds) ? { siteIds: [...filters.siteIds] } : {}),
  };
}

function hasNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** Map thin `{ id, name }` catalog rows to antd Select options (safe sort). */
export function toSelectOptions(
  rows: Array<{ id?: string | null; name?: string | null }>,
): SelectOption[] {
  const list = Array.isArray(rows) ? rows : [];
  const byValue = new Map<string, string>();
  for (const row of list) {
    if (!hasNonEmptyString(row.id) || !hasNonEmptyString(row.name)) continue;
    if (byValue.has(row.id)) continue;
    byValue.set(row.id, row.name);
  }
  return Array.from(byValue.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
}

export function mergeSelectedAssetIds(
  currentIds: string[],
  idsToAdd: string[],
): string[] {
  const merged = new Set(currentIds);
  for (const id of idsToAdd) {
    merged.add(id);
  }
  return Array.from(merged);
}

export function unresolvedConflictCount(
  conflicts: AssignmentConflictResponseDto[],
  decisions: Record<string, string | undefined>,
): number {
  return conflicts.filter((conflict) => !decisions[conflict.assetId]).length;
}

/** Preview still returns conflicts that REPLACE will resolve — filter those out. */
export function unresolvedPreviewConflicts<T extends { assetId: string }>(
  conflicts: T[],
  replaceAssetIds: string[],
): T[] {
  const replaceSet = new Set(replaceAssetIds);
  return conflicts.filter((conflict) => !replaceSet.has(conflict.assetId));
}

export function allConflictsResolved(
  conflicts: AssignmentConflictResponseDto[],
  decisions: Record<string, string | undefined>,
): boolean {
  return conflicts.every((conflict) => !!decisions[conflict.assetId]);
}

/**
 * Whether footer Continue may be clicked on Step 3.
 * Conflicts are validated on click (auto-preview), not as a pre-gate —
 * except when a check already ran and unresolved decisions remain.
 */
export function canProceedStepAssets(
  input: StepAssetsCanProceedInput,
): boolean {
  if (input.assetIds.length === 0) return true;
  if (!normalizeDatePickerString(input.startDate)) return false;
  if (
    input.conflictsChecked &&
    input.checkedConflicts.length > 0 &&
    !allConflictsResolved(input.checkedConflicts, input.conflictDecisions)
  ) {
    return false;
  }
  return true;
}

/**
 * After a successful preview (or when already checked with no unresolved),
 * whether we may advance to Review without opening the conflict modal.
 */
export function canAdvanceAfterPreview(
  input: StepAssetsCanProceedInput,
): boolean {
  if (input.assetIds.length === 0) return true;
  if (!normalizeDatePickerString(input.startDate)) return false;
  if (!input.conflictsChecked) return false;
  return allConflictsResolved(input.checkedConflicts, input.conflictDecisions);
}

/** Apply Replace / Deselect decisions to wizard assignment fields. */
export function applyConflictDecisionsToWizard(
  data: Pick<WizardData, 'assetIds' | 'checkedConflicts' | 'conflictDecisions'>,
): Pick<WizardData, 'assetIds' | 'replaceAssetIds'> {
  const replaceAssetIds = data.checkedConflicts
    .filter(
      (conflict) => data.conflictDecisions[conflict.assetId] === 'REPLACE',
    )
    .map((conflict) => conflict.assetId);
  const deselectAssetIds = new Set(
    data.checkedConflicts
      .filter(
        (conflict) => data.conflictDecisions[conflict.assetId] === 'DESELECT',
      )
      .map((conflict) => conflict.assetId),
  );

  return {
    replaceAssetIds,
    assetIds: data.assetIds.filter((assetId) => !deselectAssetIds.has(assetId)),
  };
}
